import React, { FC, useCallback, useState, useEffect, memo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { format } from 'date-fns';

import {
  Spinner,
  Text,
  Button
} from 'components/atoms';
import {
  Tabs
} from 'components/molecules';
import { gatewayService } from 'services';
import { Customer as CustomerType } from 'types/Customer';
import { Job } from 'types/Job';
import { Payment, Provider } from 'types/Payment';
import { ITableHeader, CellType } from 'types/Header';
import {
  getNetworkErrors,
  getFormattedCustomerAddress,
  formatCurrency,
  isListEmpty,
  getPaymentService
} from 'utils/general';
import {
  PageHeader,
  Table
} from '../../components';
import { useSortFilter } from '../../hooks';
import { AdminPageProps } from 'components/AppRouter';
import {
  NetworkError
} from 'types/Error';
import { theme } from 'theme';
import {
  Card,
  Header,
  AdminFormLine,
  NoDataIcon,
  NoDataCaption,
  Spacer,
  StyledPlus,
  StyledLink,
  StyledLinkExternal,
  StyledRefund
} from 'theme/mixins';

import {
  Wrapper,
  SummaryWrapper
} from './Customer.styles';

interface State {
  customer: {
    loading: boolean;
    data: CustomerType | null;
    error: NetworkError | null;
  };
  jobs: {
    loading: boolean;
    data: Job[] | null;
    total: number | null;
    fields: any;
    offset: number;
    error: NetworkError | null;
  };
  payments: {
    loading: boolean;
    data: Payment[] | null;
    total: number | null;
    fields: any;
    offset: number;
    error: NetworkError | null;
  };
  activeTab: string;
}

const initialState: State = {
  customer: {
    loading: false,
    data: null,
    error: null
  },
  jobs: {
    loading: false,
    total: 0,
    data: null,
    fields: {},
    offset: 0,
    error: null
  },
  payments: {
    loading: false,
    total: 0,
    data: null,
    fields: {},
    offset: 0,
    error: null
  },
  activeTab: ''
};


const Customer: FC<AdminPageProps> = props => {
  const {
    addToast,
  } = props;

  const navigate = useNavigate();
  const { clientId, id: customerId } = useParams();

  const [state, setState] = useState<State>(initialState);

  const {
    renderPaginationAndSort: jobPaginationMarkup,
    sortState: jobSortState,
    setSortState: jobSetSortState
  } = useSortFilter({
    listItemId: 'Booking',
    total: state.jobs.total,
    offset: state.jobs.offset,
    onPrev: (opts) => fetchJobs(opts),
    onNext: (opts) => fetchJobs(opts),
    onReset: (opts) => fetchJobs(opts),
    onFilter: (opts) => fetchJobs(opts),
    onSort: (opts) => fetchJobs(opts)
  });

  const {
    renderPaginationAndSort: paymentPaginationMarkup,
    sortState: paymentSortState,
    setSortState: paymentSetSortState
  } = useSortFilter({
    listItemId: 'Payment',
    total: state.payments.total,
    offset: state.payments.offset,
    onPrev: (opts) => fetchPayments(opts),
    onNext: (opts) => fetchPayments(opts),
    onReset: (opts) => fetchPayments(opts),
    onFilter: (opts) => fetchPayments(opts),
    onSort: (opts) => fetchPayments(opts)
  });

  const fetchCustomer = useCallback(() => {
    if (state.customer.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      customer: {
        ...prevState.customer,
        loading: true
      }
    }));

    gatewayService.getCustomerById(clientId!, customerId!)
      .then((customerResponse: any) => {
        setState(prevState => ({
          ...prevState,
          customer: {
            loading: false,
            data: customerResponse.data,
            error: null
          }
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        setState(prevState => ({
          ...prevState,
          customer: {
            ...prevState.customer,
            loading: false,
            error
          }
        }));

        addToast({
          type: 'error',
          content: error.message
        });
      });
  }, [
    state.customer,
    customerId,
    clientId,
    addToast
  ]);

  const fetchJobs = useCallback((opts?: any) => {
    if (state.jobs.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      jobs: {
        ...prevState.jobs,
        loading: true
      }
    }));

    gatewayService.getJobs(
      clientId!,
      { customerId },
      opts
    )
      .then((jobs: any) => {
        setState(prevState => ({
          ...prevState,
          jobs: {
            ...prevState.jobs,
            loading: false,
            total: jobs.total,
            data: [ ...jobs.data ],
            fields: jobs.fields,
            offset: jobs.offset,
            error: null
          }
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        setState(prevState => ({
          ...prevState,
          jobs: {
            ...prevState.jobs,
            loading: false,
            error
          }
        }));

        addToast({
          type: 'error',
          content: error.message
        });
      });
  }, [
    state.jobs,
    clientId,
    customerId,
    addToast
  ]);

  const fetchPayments = useCallback((opts?: any) => {
    if (state.payments.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      payments: {
        ...prevState.payments,
        loading: true
      }
    }));

    gatewayService.getPayments(
      clientId!,
      { customerId },
      opts
    )
      .then((paymentsResponse: any) => {
        setState(prevState => ({
          ...prevState,
          payments: {
            ...prevState.jobs,
            loading: false,
            total: paymentsResponse.total,
            data: paymentsResponse.data,
            fields: paymentsResponse.fields,
            offset: paymentsResponse.offset,
            error: null
          }
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        setState(prevState => ({
          ...prevState,
          payments: {
            ...prevState.payments,
            loading: false,
            error
          }
        }));

        addToast({
          type: 'error',
          content: error.message
        });
      });
  }, [
    state.payments,
    clientId,
    customerId,
    addToast
  ]);

  const getBookingsHeaderConfig = useCallback((): ITableHeader[] => {
    return [
      {
        key: '_id',
        label: 'Job',
        width: '30%',
        type: CellType.Text,
        format: (value: string) => (
          <StyledLink
            to={`/${clientId}/back-office/jobs/${value}`}
          >{value}</StyledLink>
        )
      },
      {
        key: 'reference',
        label: 'Ref',
        width: '30%',
        type: CellType.Text
      },
      {
        key: 'status',
        label: 'Status',
        width: '40%',
        type: CellType.Text,
        sortable: true,
        format: (status) => status.slice(-1)[0].type
      },
      {
        key: 'amountDue',
        label: 'Balance',
        width: '30%',
        sortable: true,
        type: CellType.Currency,
        format: (value: number) => formatCurrency(value)
      },
      {
        key: 'fulfiller',
        label: state.customer.data?.client?.settings.jobs.defaultFulfillerLabel,
        width: '30%',
        type: CellType.Text,
        format: (fulfiller) => {
          if (!fulfiller) {
            return 'Unmatched';
          }

          return `${fulfiller.firstName} ${fulfiller.lastName}`
        }
      },
    ];
  }, [
    clientId,
    state.customer.data
  ]);

  const getPaymentsHeaderConfig = useCallback((): ITableHeader[] => {
    return [
      {
        key: 'paymentId',
        label: `${getPaymentService(state.customer.data?.client?.customerPaymentService?.type || null)} ID`,
        width: '30%',
        type: CellType.Text,
        format: (value: string, row) => {
          if (row.provider === Provider.External) {
            return row._id;
          }

          if (!state.customer.data?.client.customerPaymentService) {
            return <i>Add customer payment service</i>
          }

          return (
            <StyledLinkExternal
              inline
              href={`${state.customer.data?.client.customerPaymentService.paymentsDashboardLink}/${value}`}
              target={'_blank'}
            >{value}</StyledLinkExternal>
          );
        }
      },
      {
        key: 'job._id',
        label: 'Job',
        width: '17%',
        type: CellType.Text,
        format: (value: string) => (
          <StyledLink
            to={`/${clientId}/back-office/jobs/${value}`}
          >{value}</StyledLink>
        )
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        type: CellType.Currency,
        format: (value: number) => formatCurrency(value)
      },
      {
        key: 'job.reference',
        label: 'Ref',
        type: CellType.Text
      },
      {
        key: 'date',
        label: 'Date',
        width: '17%',
        type: CellType.Text,
        format: (value: string) => format(new Date(value), 'HH:mm dd/MM/yyyy')
      },
      {
        key: 'refunded',
        width: '5%',
        type: CellType.Icon,
        renderIcon: (row) => {
          // TODO: if on mobile open modal to show information
          if (row.refunded) {
            return (
              <div
                data-title={row.provider !== Provider.External ? `Refund initiated. It may take a few days to clear. Check the status with your payment service.` : 'Refunded'}
                style={{ display: 'inline-block' }}
              >
                <StyledRefund />
              </div>
            );
          }

          return '';
        }
      }
    ];
  }, [
    clientId,
    state.customer.data
  ]);

  useEffect(() => {
    if (!state.customer.data && !state.customer.error) {
      fetchCustomer();
    }

    if (!state.jobs.data && !state.jobs.error) {
      fetchJobs();
    }

    if (!state.payments.data && !state.payments.error) {
      fetchPayments();
    }
  }, [
    state.customer.data,
    state.customer.error,
    state.jobs.data,
    state.jobs.error,
    state.payments.data,
    state.payments.error,
    fetchCustomer,
    fetchJobs,
    fetchPayments
  ]);

  if (state.customer.loading) {
    return (
      <Spinner
        color={theme.textColor}
        size={'M'}
      />
    );
  }

  return (
    <Wrapper>
      <PageHeader
        title={'Customer'}
      />
      <Card>
        <SummaryWrapper>
          <div>
            <Header>{state.customer.data?.firstName} {state.customer.data?.lastName}</Header>
            <AdminFormLine marginBottom>
              <Text
                label="Company"
                value={state.customer.data?.businessName}
              />
            </AdminFormLine>
            <AdminFormLine marginBottom>
              <Text
                label="Billing address"
                value={getFormattedCustomerAddress(state.customer.data)}
              />
            </AdminFormLine>
          </div>
          <div>
            <h3>Summary</h3>
            <AdminFormLine marginBottom>
              <Text
                label="Balance"
                value={formatCurrency(state.customer.data?.balance || 0)}
              />
            </AdminFormLine>
            <AdminFormLine marginBottom>
              <Text
                label="Bookings"
                value={state.jobs.total}
              />
            </AdminFormLine>
          </div>
        </SummaryWrapper>
        <Spacer height="3rem" />
        <Tabs
          activeTab={state.activeTab}
          tabHasChanged={(activeTab: string, index: number) => {
            setState(prevState => ({
              ...prevState,
              activeTab
            }));
          }}
        >
          <div title={'Bookings'}>
            <AdminFormLine
              column
              marginTop
            >
              <AdminFormLine row centerV marginBottom>
                {jobPaginationMarkup()}
              </AdminFormLine>
              {state.jobs.loading ? (
                <Spinner
                  color={theme.textColor}
                  size={'M'}
                />
              ) : isListEmpty(state.jobs.data) ? (
                <>
                  <AdminFormLine
                    column
                    centerH
                  >
                    <NoDataIcon />
                    <AdminFormLine marginBottom>
                      <NoDataCaption>Create the first booking for this customer</NoDataCaption>
                    </AdminFormLine>
                    <Button
                      icon={<StyledPlus />}
                      onClick={() => {
                        navigate(`/${clientId}/back-office/jobs/create`);
                      }}
                    >Create a booking</Button>
                  </AdminFormLine>
                </>
              ) : (
                <Table
                  namespaceKey={'bookings'}
                  loading={state.jobs.loading}
                  headerConfig={getBookingsHeaderConfig()}
                  rows={state.jobs.data || []}
                  total={state.jobs.total!}
                  offset={state.jobs.offset}
                  footerContent={jobPaginationMarkup()}
                  sortState={jobSortState}
                  onSort={jobSetSortState}
                />
              )}
            </AdminFormLine>
          </div>
          <div title={'Payments'}>
            <AdminFormLine
              column
              marginTop
              marginBottom
            >
              {state.payments.loading ? (
                <Spinner
                  color={theme.textColor}
                  size={'M'}
                />
              ) : isListEmpty(state.payments.data) ? (
                <>
                  <AdminFormLine
                    column
                    centerH
                  >
                    <NoDataIcon />
                    <AdminFormLine marginBottom>
                      <NoDataCaption>No payments</NoDataCaption>
                    </AdminFormLine>
                  </AdminFormLine>
                </>
              ) : (
                <Table
                  namespaceKey={'payments'}
                  loading={state.payments.loading}
                  headerConfig={getPaymentsHeaderConfig()}
                  rows={state.payments.data || []}
                  total={state.payments.total!}
                  offset={state.payments.offset}
                  footerContent={paymentPaginationMarkup()}
                  sortState={paymentSortState}
                  onSort={paymentSetSortState}
                />
              )}
            </AdminFormLine>
          </div>
        </Tabs>
      </Card>
    </Wrapper>
  );
};

export default memo(Customer);

